// Third party
import React from 'react';
import Modal from 'react-bootstrap/Modal';
import PerfectScrollbar from 'react-perfect-scrollbar';
import moment from 'moment';

// Components
import CloseButton from '../../buttons/CloseButton';
import { ArrowIcon } from './icons/ArrowIcon';
import DrawIcon from './icons/DrawIcon';
import AutoGraphIcon from './icons/AutographIcon';
import LaptopIcon from './icons/LaptopIcon';
import LockIcon from './icons/LockIcon';
import DocumentIcon from './icons/DocumentIcon';
import { Tooltip } from '../../tooltips';
import { LoadingSpinner } from '../../common';
import { Typography } from '@capintel/component-lib/typography';

// Types
import Locale from '@/types/locale';
import Presentation from '@/types/presentation';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Utils
import { MODAL_SIZES } from '@/utils/constants';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { getOmniLocale } from '@/utils/locale';

type ExportPresentationModalV2Props = {
  handleOnCreateOmniProposal: () => void;
  handleOnCreatePDFProposal: () => void;
  presentationsHistory?: Presentation[] | undefined | null;
  disableOmni: boolean;
  canCreateOmniProposal: {
    isDirty?: boolean;
    hasContact?: boolean;
  };
  presentationTemplates: string[];
  locale: Locale;
  show: boolean;
  onClose: () => void;
};

const ExportPresentationModalV2: React.FC<ExportPresentationModalV2Props> = ({
  handleOnCreatePDFProposal,
  handleOnCreateOmniProposal,
  presentationsHistory,
  disableOmni,
  canCreateOmniProposal,
  presentationTemplates,
  locale,
  show,
  onClose,
}) => {
  const getOmniTooltip = () => {
    if (canCreateOmniProposal.isDirty) {
      return formatMessage('CreatePresentationModal.Online.UnsavedChanges');
    }

    if (!presentationTemplates?.length) {
      return formatMessage('CreatePresentationModal.Online.NoPresentations');
    }

    return undefined;
  };

  const renderResults = () => {
    if (presentationsHistory === undefined) {
      return <LoadingSpinner className="online-presentation-loading" />;
    }

    if (presentationsHistory === null) {
      return (
        <p className="online-presentation-message error">
          <FormattedMessage id="CreatePresentationModal.PresentationHistory.Error" />
        </p>
      );
    }

    if (presentationsHistory?.length === 0) {
      return (
        <p className="online-presentation-message">
          <FormattedMessage id="CreatePresentationModal.PresentationHistory.Empty" />
        </p>
      );
    }

    return (
      <div>
        <p className="online-presentation-message">
          <FormattedMessage id="CreatePresentationModal.PresentationHistory.Subtitle" />
        </p>
        <PerfectScrollbar className="presentations-list" style={{ '--visible-count': 2 } as React.CSSProperties}>
          <ul>
            {presentationsHistory?.map(presentation => (
              <li key={presentation.id}>
                <a href={presentation.url.replace('/builder', `/${getOmniLocale(locale)}/builder`)}>
                  <div className="presentation-item-header">
                    <div className="title">{presentation.title}</div>
                    {presentation.readOnly && (
                      <span className="pill">
                        <LockIcon />
                      </span>
                    )}
                  </div>
                  <p className="date">
                    <FormattedMessage
                      id="CreatePresentationModal.PresentationHistory.CreatedAtDate"
                      values={{ date: moment(presentation.createdAt).format(formatMessage('Common.DateFormats.Short')) }}
                    />
                  </p>
                </a>
              </li>
            ))}
          </ul>
        </PerfectScrollbar>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onClose} className="export-presentation-modal-v2" dialogClassName="modal-dialog-centered" size={MODAL_SIZES.SM}>
      <Modal.Header>
        {/* Provisional fix. Pending migration to library modal. */}
        <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="headerXs">
          <FormattedMessage id="CreatePresentationModal.Title" />
        </Typography>
        <CloseButton onClick={onClose} />
      </Modal.Header>

      <Modal.Body>
        <div className="export-presentation-button-container">
          <Tooltip disabled={!disableOmni} place="left" message={getOmniTooltip()}>
            <div className="omni-button-wrapper">
              <div className="omni-background" />
              <button className="export-presentation-button export-omni-button" onClick={handleOnCreateOmniProposal} disabled={disableOmni}>
                <div className="export-presentation-icons" aria-hidden="true">
                  <div className="omni-icons-container">
                    <div className="omni-draw-container omni-icon">
                      <DrawIcon />
                    </div>
                    <div className="omni-auto-graph-container omni-icon">
                      <AutoGraphIcon />
                    </div>
                    <div className="omni-laptop-container omni-icon">
                      <LaptopIcon />
                    </div>
                  </div>
                  <ArrowIcon size="large" />
                </div>

                <div className="export-presentation-button-label">
                  <FormattedMessage id="CreatePresentationModal.Online.Label" />
                </div>
                <div className="export-presentation-button-description">
                  <FormattedMessage id="CreatePresentationModal.Online.Description" />
                </div>
              </button>
            </div>
          </Tooltip>

          <button className="export-presentation-button export-pdf-button" onClick={handleOnCreatePDFProposal}>
            <div className="export-presentation-icons" aria-hidden="true">
              <DocumentIcon />
              <ArrowIcon size="large" />
            </div>
            <div className="export-presentation-button-label">
              <FormattedMessage id="CreatePresentationModal.PDF.Label" />
            </div>
            <div className="export-presentation-button-description">
              <FormattedMessage id="CreatePresentationModal.PDF.Description" />
            </div>
          </button>
        </div>
        <div>
          <h3 className="online-presentation-header">
            <FormattedMessage id="CreatePresentationModal.PresentationHistory.Title" />
          </h3>
          {renderResults()}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ExportPresentationModalV2;
